import { Button, Col, Divider, Drawer, Image, Row, Tag } from "antd";
import { useCallback, useEffect, useState } from "react";
import { EyeOutlined } from "@ant-design/icons";
import { deleteDocument, getDocuments } from "../../../services/exam";
import { getAsset, removeAsset } from "../../../services/general";
import { useTranslation } from "react-i18next";
import countrySettings from "../countrySettings.json"
import { getCookie } from "../../../services/cookies";
import ExamDetailPage from "../ExamDetailPage";

const MedicalConsultationHeader = ({
  admissionPatientExamId,
  fetchMessage,
  setLoading,
  examName,
  hasPreviousExam,
  parentAdmissionPatientExamId,
  parentAdmissionPatientId,
  groupType,
  serviceParams,
}) => {
  const [documentsState, setDocuments] = useState([]);
  const [documentUrls, setDocumentUrls] = useState({});
  const [previousExamDrawerVisible, setPreviousExamDrawerVisible] = useState(false);
  const { t } = useTranslation();
  const countryCode = getCookie("country_code");

  const isImageDocument = (name) => /\.(jpg|jpeg|png|gif|webp)$/i.test(name);

  const fetchDocuments = useCallback(async () => {
    setLoading(state => ({ ...state, documents: true }));
    try {
      const documents = await getDocuments({ admissionPatientExamId, type: 'exams' });
      setDocuments(documents);
      const imageDocuments = documents.filter(doc => isImageDocument(doc.document_name));
      const entries = await Promise.all(
        imageDocuments.map(async (doc) => {
          const result = await getAsset({ field: 'document', id: doc.id, type: doc.type });
          return [doc.id, result.document_url];
        })
      );
      setDocumentUrls(Object.fromEntries(entries));
    } catch (error) {
      console.error('Error', error);
      fetchMessage('Error', 'Error al obtener los documentos');
    } finally {
      setLoading(state => ({ ...state, documents: false }));
    }
  }, [admissionPatientExamId]);

  useEffect(() => {
    if (admissionPatientExamId) {
      fetchDocuments();
    }
  }, [admissionPatientExamId])

  const handleRemoveDocument = async (document_id) => {
    setLoading(state => ({ ...state, documents: true }));
    try {
      const [removeResult, deleteResult] = await Promise.all([
        removeAsset({
          field: 'document',
          id: document_id,
          type: 'ExamsDocument',
        }),
        deleteDocument({
          examsDocumentId: document_id,
        })
      ]);

      if (removeResult?.success && deleteResult) {
        fetchMessage('success', 'Documento eliminado correctamente')
      }
      await fetchDocuments();
    } catch (error) {
      console.error('Error', error);
      fetchMessage('Error', 'Error al eliminar el documento');
    } finally {
      setLoading(state => ({ ...state, documents: false }));
    }
  }

  const handleDownloadDocument = async (examDocument) => {
    setLoading(loading => ({ ...loading, documents: true }));
    try {
      const url = await getAsset({
        field: 'document',
        id: examDocument.id,
        type: examDocument.type,
      });
      const link = document.createElement("a");
      link.href = url.document_url;
      link.download = examDocument.document_name;
      link.setAttribute('target', '_blank');
      link.click();
    } catch (error) {
      console.error('Error', error);
      fetchMessage('Error', 'Error al descargar el documento');
    } finally {
      setLoading(loading => ({ ...loading, documents: false }));
    }
  };

  const docsBlock = (
    <Row className="header pt-20 pb-20 mb-20" gutter={[10, 16]}>
      <Col lg={24}>
        <p className="mb-0">Documentos: {documentsState.length === 0 && <strong>Sin documentos</strong>}</p>
      </Col>
      {countrySettings.uploadDocumentsModal[countryCode] && (
        <Col lg={24}>
          <Row className="documents-legend">
            <Col className='mr-40'>
              <Row align="middle">
                <div className="dot internal mr-10" />
                <p className="mb-0">{t('examPage.header.internalDocument')}</p>
              </Row>
            </Col>
            <Col>
              <Row align="middle">
                <div className="dot report mr-10" />
                <p className="mb-0">{t('examPage.header.reportDocument')}</p>
              </Row>
            </Col>
          </Row>
        </Col>
      )}
      {
        documentsState.some(document => !isImageDocument(document.document_name)) && (
          <Col lg={24}>
            <Row align="middle" gutter={[10, 10]}>
              {documentsState
                .filter(document => !isImageDocument(document.document_name))
                .map((document) => (
                  <Col key={document.id}>
                    <Tag
                      className={`attachments ${document.add_to_report ? "report-document" : ""}`}
                      closable
                      onClose={() => handleRemoveDocument(document.id)}
                    >
                      <span
                        className="label"
                        onClick={() => { handleDownloadDocument(document) }}
                      >
                        {document.document_name}
                      </span>
                    </Tag>
                  </Col>
                ))}
            </Row>
          </Col>
        )
      }
      {
        documentsState.some(document => isImageDocument(document.document_name)) && (
          <Col lg={24}>
            <Row align="middle" gutter={[10, 10]}>
              {documentsState
                .filter(document => isImageDocument(document.document_name))
                .map((document) => {
                  const url = documentUrls[document.id];
                  if (!url) return null;
                  return (
                    <Col key={document.id}>
                      <div className={`img-attachment ${document.add_to_report ? "report-img-attachment" : ""}`}>
                        <Image src={url} alt={document.document_name} />
                        <button
                          type="button"
                          className="img-attachment-delete"
                          onClick={() => handleRemoveDocument(document.id)}
                        >
                          <span className="icon-system-delete" title="icon-system-delete" />
                        </button>
                      </div>
                    </Col>
                  );
                })}
            </Row>
          </Col>
        )
      }
    </Row>
  );

  if (examName) {
    return (
      <div className="exam-header-with-title">
        <Divider className="exam-content-divider" />
        <Row align="middle" justify="space-between" className="mb-10">
          <Col>
            <p className="exam-section-title mb-0">{examName}</p>
          </Col>
          {hasPreviousExam && countrySettings.previousExamButton[countryCode] && (
            <Col>
              <Button
                className="cmt-button primary outline"
                icon={<EyeOutlined />}
                onClick={() => setPreviousExamDrawerVisible(true)}
              >
                Examen anterior
              </Button>
            </Col>
          )}
        </Row>
        {docsBlock}
        {hasPreviousExam && countrySettings.previousExamButton[countryCode] && (
          <Drawer
            className="exam-detail-drawer"
            title="Examen anterior"
            placement="right"
            width="74%"
            open={previousExamDrawerVisible}
            onClose={() => setPreviousExamDrawerVisible(false)}
            closable={true}
            destroyOnClose
          >
            <ExamDetailPage
              from={'expert'}
              fromExternal={true}
              externalExam={{
                admission_patient_id: parentAdmissionPatientId,
              }}
              externalAdmissionPatientExamId={groupType ? undefined : parentAdmissionPatientExamId}
              externalGroupType={groupType || undefined}
              serviceParams={serviceParams}
              readOnly={true}
            />
          </Drawer>
        )}
      </div>
    );
  }

  return docsBlock;
}

export default MedicalConsultationHeader;
